<template>
  <div class="wrapper xl:flex-1 xl:flex xl:overflow-y-hidden">
    <filter-layout @filtered="filterChanged"></filter-layout>

    <div class="py-6 xl:flex-1 flex-col overflow-x-hidden">
      <div class="px-4 xl:px-8 lg:mb-8" v-if="$subject.isAllowed('matrix')">
        <div class="buttons flex justify-end">
          <span class="relative z-0 inline-flex shadow-sm" v-if="(isFiltered || isSearched) && subjects.length">
            <button type="button" class="relative inline-flex items-center px-6 py-3 rounded-l bg-green-dark font-semibold text-sm leading-none text-white" @click="exportAll">
              Exporteer alles
            </button>
            <span class="-ml-px relative block">
              <button type="button" @click="exportBtnIsOpen = !exportBtnIsOpen" class="bg-green-dark border-green-lightest border-l h-full inline-flex items-center px-2 relative rounded-r text-white">
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
              <div v-show="exportBtnIsOpen" class="absolute bg-white mt-1 pin-r rounded shadow z-30 w-48">
                <div class="px-2 py-2 text-sm text-grey-darker">
                  <button type="button" @click="downloadAll" class="inline-block w-full px-3 py-2 hover:bg-grey-lighter rounded cursor-pointer text-left">
                    Download alles
                  </button>
                </div>
              </div>
            </span>
          </span>
          <button class="hidden lg:inline-block leading-none rounded px-8 py-3 text-sm text-white font-semibold ml-4" :disabled="!selected" :class="{ 'bg-grey-dark': !selected, 'bg-blue-dark': selected }" @click="exportSelected()">
            Exporteer
          </button>
        </div>
      </div>

      <div class="flex flex-col px-4 lg:px-0 xl:px-8">
        <table class="w-full table-fixed md:table-auto lg:shadow overflow-x-auto" data-cy="homepage table" ref="SubjectTable">
          <thead>
            <tr class="bg-white">
              <th class="w-2 px-4" v-if="$subject.isAllowed('matrix')">
                <input type="checkbox" :checked="isSelectAll" :indeterminate.prop="isIndeterminate" @click="selectAll()" />
              </th>
              <th v-for="(header, index) in headers" :key="index" class="px-2" v-on="header.order ? { click: () => orderChanged(header.value) } : {}" :class="{ 'active': isOrderActive(header.value) }">
                <span class="cursor-pointer hover:text-green-dark mr-1">
                  {{ header.label }}
                </span>
                <font-awesome-icon v-show="isOrderActive(header.value)" :icon="showArrow" size="lg" :transform="arrowTransform" />
              </th>
            </tr>
          </thead>
          <tbody v-if="subjects.length">
            <template v-for="subject in subjects">
              <tr class="bg-white hover:bg-grey-lightest text-grey-darker object cursor-pointer" :key="`subject-${subject.id}`" @click="navigate(subject.id)">
                <td class="w-2 px-4 border-t border-grey-lighter pt-4" v-if="$subject.isAllowed('matrix')">
                  <input type="checkbox" v-model="selectedSubjectIds" :value="subject.id" @click.stop @change="select" />
                </td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Opdrachtgever">{{ subject.inventories && subject.inventories[0].order.client.name }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4">{{ subject.inventories && subject.inventories[0].order.contact }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Straat">{{ subject.street }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Huisnummer">{{ subject.house_number }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Postcode">{{ subject.postal_code }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Plaats">{{ subject.city }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4">{{ subject.inventories && subject.inventories[0].order.project_number }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4">{{ subject.inventories && subject.inventories[0].order.status.name }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4">{{ subject.inventories && subject.inventories[0].investigator.name }}</td>
                <td class="border-t border-grey-lighter px-2 pt-4" data-label="Plattegrond">
                  <template v-if="subject.map">
                    <font-awesome-icon :icon="['far', 'map']" class="text-lg md:text-2xl" @click.stop="requestFileUrl('show', subject.map.id)" />
                  </template>
                </td>
              </tr>
              <tr class="bg-white cursor-pointer" :key="`bronnen-${subject.id}`" @click="navigate(subject.id)">
                <td class="w-2 px-4" v-if="$subject.isAllowed('matrix')">
                <td class="pl-2 lg:pt-0 border-t border-grey-lighter lg:border-0" colspan="11" data-label="Bronnen" v-if="hasSources(subject)">
                  <div class="hidden lg:inline-block text-grey-darkest font-medium mr-2">Bronnen:</div>
                  <div class="lg:inline-block">
                    <template v-for="child in subject.children">
                      <span v-for="source in child.sources" :key="source.id" :style="{ color: source.status.color }">
                        {{ source.name }}
                      </span>
                    </template>
                  </div>
                </td>
                <td class="pl-2 lg:pt-0 border-t border-grey-lighter lg:border-0" colspan="11" data-label="Bronnen" v-else>
                  <div class="hidden lg:inline-block text-grey-darkest font-medium mr-2">Bronnen:</div>
                  <span>-</span>
                </td>
              </tr>
            </template>
          </tbody>
          <tbody v-else>
            <tr class="bg-white">
              <td data-label="Resultaat" colspan="12">Geen objecten gevonden.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <base-pagination
        v-if="pagination && pagination.last_page > 1"
        :total-pages="pagination.last_page"
        :total="10"
        :current-page="pagination.current_page"
        @page-changed="pageChanged">
      </base-pagination>
      <base-notification
        :type="notification.type"
        :message.sync="notification.message"
      ></base-notification>
    </div>
  </div>
</template>

<script>
import router from '@/router';
import FilterLayout from '@/components/filters/FilterLayout';
import ApiService from '@/common/api.service';
import MatrixService from '@/services/matrix';
import SubjectPerimeter from '@/perimeters/subject';
import BaseNotification from '@/components/globals/BaseNotification';

import { API_URL } from '@/common/config';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapState } from 'vuex';

import store from '@/store';
import subject from '@/store/modules/subject';

if (!store.state.subject) store.registerModule('subject', subject);

export default {
  name: 'SubjectList',
  components: {
    FilterLayout,
    FontAwesomeIcon,
    BaseNotification
  },
  perimeters: [SubjectPerimeter],
  data() {
    return {
      isFiltered: false,
      selected: false,
      selectedSubjectIds: [],
      exportBtnIsOpen: false,
      headers: [
        { label: 'Opdrachtgever', value: 'client', order: true },
        { label: 'Contactpersoon', value: 'contact', order: true },
        { label: 'Straat', value: 'street', order: true },
        { label: 'Nummer', value: 'house_number', order: true },
        { label: 'Postcode', value: 'postal_code', order: true },
        { label: 'Plaats', value: 'city', order: true },
        { label: 'Projectnr.', value: 'project_nr', order: true },
        { label: 'Status', value: 'status', order: false },
        { label: 'Onderzoeker', value: 'investigator', order: true },
        { label: '', value: '', order: false }
      ],
      notification: {
        type: 'info',
        message: ''
      }
    };
  },
  created() {
    if (this.$route.params.term) {
      this.$store.commit('subject/setFilters', {
        key: 'filter[q]',
        value: this.$route.params.term
      });
    }

    this.$store.dispatch('subject/fetchAll');
  },
  computed: {
    ...mapState('subject', {
      pagination: state => state.pagination,
      subjects: state => state.items,
      filters: state => state.filters
    }),
    showArrow() {
      return this.filters.reverse.value
        ? ['fas', 'sort-down']
        : ['fas', 'sort-up'];
    },
    arrowTransform() {
      return this.filters.reverse.value ? 'up-3' : 'down-3';
    },
    isIndeterminate() {
      return this.selectedSubjectIds.length > 0 && !this.isSelectAll;
    },
    isSelectAll() {
      return (
        this.subjects &&
        this.subjects.length > 0 &&
        this.selectedSubjectIds.length === this.subjects.length
      );
    },
    isSearched() {
      return this.filters && this.filters['filter[q]'];
    }
  },
  methods: {
    select(event) {
      event.target.parentNode.parentNode.classList.toggle('selected');
      if (this.selectedSubjectIds.length < 1) return (this.selected = false);
      this.selected = true;
    },
    selectAll() {
      let rows = this.$refs.SubjectTable.tBodies[0].rows;
      let length = rows.length;

      if (this.isIndeterminate || this.isSelectAll) {
        for (let i = 0; i < length; i++) {
          rows[i].classList.remove('selected');
        }
        this.selectedSubjectIds = [];
        this.selected = false;
      } else {
        for (let i = 0; i < length; i++) {
          rows[i].classList.add('selected');
        }
        this.selectedSubjectIds = this.subjects.map(subject => subject.id);
        this.selected = true;
      }
    },
    pageChanged: function(page) {
      this.$store.commit('subject/setFilters', { key: 'page', value: page });
      this.fetch();
    },
    orderChanged(key) {
      let reverse = this.isOrderActive(key)
        ? !this.filters.reverse.value
        : false;
      let name = reverse ? `-${key}` : key;

      this.$store.commit('subject/setFilters', {
        key: 'reverse',
        value: reverse
      });
      this.$store.commit('subject/setFilters', { key: 'sort', value: name });
      this.fetch();
    },
    async filterChanged() {
      await this.fetch();
      this.isFiltered =
        Object.keys(this.filters).length > 3 && this.subjects.length > 0;
    },
    isOrderActive(name) {
      return this.filters.sort.value.replace('-', '') == name;
    },
    navigate(id) {
      return router.push({ name: 'subject', params: { id: id } });
    },
    fetch() {
      return this.$store.dispatch('subject/fetchAll');
    },
    hasSources(subject) {
      return subject && subject.hasOwnProperty('children');
    },
    async requestFileUrl(type, fileId) {
      const win = window.open();

      const { data } = await ApiService.get(`/v1/files/${fileId}/hash`);

      win.location = `${API_URL}/files/${type}/${data}`;
      win.focus();
    },
    async exportSelected() {
      if (!this.selected && !this.selectedSubjectIds.length) return;

      try {
        await MatrixService.create(this.selectedSubjectIds, 'subjects');

        this.notification.type = 'success';
        this.notification.message =
          'Export wordt gemaakt, controleer uw e-mail';

        return;
      } catch (response) {
        this.error = response.data;

        this.notification.type = 'error';
        this.notification.message = 'Export niet gemaakt';
      }
    },
    async exportAll() {
      if (!this.isFiltered && !this.isSearched) return;

      try {
        await MatrixService.all(this.filters, 'job');

        this.notification.type = 'success';
        this.notification.message =
          'Export wordt gemaakt, controleer uw e-mail';

        return;
      } catch (response) {
        this.error = response.data;

        this.notification.type = 'error';
        this.notification.message = 'Export niet gemaakt';
      }
    },
    async downloadAll() {
      if (!this.isFiltered && !this.isSearched) return;

      try {
        this.exportBtnIsOpen = false;

        const response = await MatrixService.all(this.filters, 'direct');
        this.download(response);
      } catch (response) {
        this.error = response.data;

        this.notification.type = 'error';
        this.notification.message = 'Export niet gemaakt';
      }
    },
    download(file) {
      MatrixService.download(
        file.data,
        file.headers['content-type'],
        'matrix.xlsx'
      );
    }
  }
};
</script>
